import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"
import { Tabs } from "components/navigation/Tabs"

import { usePermissions } from "contexts/PermissionsContext"

import { PERMISSIONS } from "constants/permissions"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CMS_LOCALE_NAMES, CmsPage, CmsPageListItem } from "services/cms-seo/type"

import { AdvancedPane } from "./editor/AdvancedPane"
import { ContentPane } from "./editor/ContentPane"
import { CustomCodePane } from "./editor/CustomCodePane"
import { EditorRail } from "./editor/EditorRail"
import { LocaleBar } from "./editor/LocaleBar"
import { SchemaPane } from "./editor/SchemaPane"
import { SeoPane } from "./editor/SeoPane"
import { SocialPane } from "./editor/SocialPane"
import { computeSeoScore } from "./helpers/seoScore"
import { useBrandSite } from "./BrandSiteContext"
import { ghostButtonClass, modalBoxClass, PathText, primaryButtonClass, StatusBadge} from "./CmsUi"
import { MediaPickerModal } from "./MediaPickerModal"
import { VersionHistoryModal } from "./VersionHistoryModal"

type EditorTab = "content" | "seo" | "social" | "schema" | "code" | "advanced"

interface PageEditorProps {
  pageId: string
  brand: string
  parentCandidates: CmsPageListItem[]
  authors: string[]
  onBack: () => void
  onSaved: () => void
  /** Opens a different page in the same editor. */
  onNavigate: (pageId: string) => void
}

export const PageEditor: FC<PageEditorProps> = ({
  pageId,
  brand,
  parentCandidates,
  authors,
  onBack,
  onSaved,
  onNavigate
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { has } = usePermissions()
  const site = useBrandSite()

  const [page, setPage] = useState<CmsPage | null>(null)
  const [original, setOriginal] = useState<CmsPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<EditorTab>("content")
  const [slugWarning, setSlugWarning] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false)
  const [publishPrompt, setPublishPrompt] = useState<CmsPage | null>(null)
  const [translating, setTranslating] = useState(false)
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null)

  const canEditCode = has(PERMISSIONS.PAGE_CMS_ADMIN_TABS)

  const load = useCallback(async () => {
    setLoading(true)
    const loaded = await cmsSeoService.getPage(pageId)
    setPage(loaded)
    setOriginal(loaded)
    setLoading(false)
  }, [pageId])

  useEffect(() => {
    load()
  }, [load])

  const change = (patch: Partial<CmsPage>) => setPage((prev) => (prev ? { ...prev, ...patch } : prev))

  const slugRenamed = Boolean(original && page && original.status === "published" && original.slug !== page.slug)

  useEffect(() => {
    if (!page) return
    let cancelled = false

    const check = async () => {
      const result = await cmsSeoService.validateSlug({
        id: page.id,
        locale: page.locale,
        slug: page.slug,
        parent_id: page.parent_id
      })
      if (cancelled) return
      setSlugWarning(result && !result.available ? result.reason : null)
    }

    const timer = setTimeout(check, 400)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [page?.id, page?.slug, page?.parent_id, page?.locale])

  const score = useMemo(() => (page ? computeSeoScore(page) : null), [page])

  const dirty = useMemo(() => JSON.stringify(page) !== JSON.stringify(original), [page, original])

  const saveDraft = async () => {
    if (!page) return
    setSaving(true)
    const saved = await cmsSeoService.updatePage(page.id, { ...page, status: page.status === "published" ? "published" : "draft" })
    setSaving(false)
    if (saved) {
      setPage(saved)
      setOriginal(saved)
      toast.success(t("cmsSeo.draftSaved", { defaultValue: "Draft saved" }) as string)
      onSaved()
    }
  }

  const publish = async (createRedirect: boolean) => {
    if (!page) return
    setSaving(true)
    const saved = await cmsSeoService.updatePage(page.id, page)
    if (!saved) {
      setSaving(false)
      return
    }
    const published = await cmsSeoService.publishPage(page.id, { create_redirect: createRedirect })
    setSaving(false)
    if (published) {
      setPage(published)
      setOriginal(published)
      toast.success(t("cmsSeo.published", { defaultValue: "Page published" }) as string)
      onSaved()
    }
  }

  const onPublishClick = () => {
    if (!page) return
    if (slugRenamed) {
      setPublishPrompt(page)
      return
    }
    publish(false)
  }

  const openPreview = async () => {
    if (!page) return
    const url = await cmsSeoService.previewUrl(page.id)
    if (url) window.open(url, "_blank", "noreferrer")
  }

  /** Opens it straight away: asking for the Spanish page means wanting to edit it. */
  const createTranslation = async (toLocale: string) => {
    if (!page || translating) return

    setTranslating(true)
    const copy = await cmsSeoService.duplicatePage(page.id, { to_locale: toLocale })
    setTranslating(false)

    if (copy) {
      toast.success(
        t("cmsSeo.translationCreated", {
          defaultValue: "{{name}} version created as a draft",
          name: CMS_LOCALE_NAMES[toLocale] ?? toLocale
        }) as string
      )
      onSaved()
      onNavigate(copy.id)
    }
  }

  /** Leaves the editor, so unsaved work is confirmed first. */
  const switchTo = (siblingId: string) => {
    if (dirty) {
      setPendingSwitch(siblingId)
      return
    }
    onNavigate(siblingId)
  }

  if (loading) {
    return <p className="py-12 text-center text-sm text-black dark:text-white">{t("common.loading", { defaultValue: "Loading..." })}</p>
  }

  if (!page || !score) {
    return (
      <div className="p-6">
        <p className="text-sm text-black dark:text-white">{t("cmsSeo.pageNotFound", { defaultValue: "That page no longer exists." })}</p>
        <button type="button" className={`${ghostButtonClass} mt-3`} onClick={onBack}>
          {t("common.close", { defaultValue: "Close" })}
        </button>
      </div>
    )
  }

  const tabs = [
    { key: "content", label: t("cmsSeo.tab.content", { defaultValue: "Content" }) },
    { key: "seo", label: t("cmsSeo.tab.seo", { defaultValue: "SEO" }) },
    { key: "social", label: t("cmsSeo.tab.social", { defaultValue: "Social" }) },
    { key: "schema", label: t("cmsSeo.tab.schema", { defaultValue: "Schema" }) },
    { key: "code", label: t("cmsSeo.tab.code", { defaultValue: "Custom code" }) },
    { key: "advanced", label: t("cmsSeo.tab.advanced", { defaultValue: "Advanced" }) }
  ]

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[rgba(0,0,0,0.05)] px-4 py-4 sm:px-6 sm:py-6 dark:border-[rgba(255,255,255,0.1)]">
        <div className="min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="mb-2 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <span aria-hidden="true">←</span>
            {t("cmsSeo.backToList", { defaultValue: "Back to pages" })}
          </button>

          <h2 className="mb-2 text-xl font-bold break-words text-gray-900 sm:text-2xl dark:text-white">
            {page.title || t("cmsSeo.untitled", { defaultValue: "Untitled page" })}
          </h2>

          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge status={page.status} />
            {/* A relative href would point at the panel, not the site. */}
            {site.origin ? (
              <a
                href={site.url(page.locale, page.path)}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-gray-500 hover:underline dark:text-gray-400"
              >
                {site.host}/{page.locale}
                <PathText>{page.path === "/" ? "" : page.path}</PathText>
              </a>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                /{page.locale}
                <PathText>{page.path === "/" ? "" : page.path}</PathText>
              </span>
            )}
          </div>
        </div>

        <div className="-mx-4 flex w-full shrink-0 items-center gap-2.5 overflow-x-auto px-4 sm:mx-0 sm:w-auto sm:px-0">
          <button type="button" className={`${ghostButtonClass} shrink-0`} onClick={() => setHistoryOpen(true)}>
            {t("cmsSeo.history", { defaultValue: "History" })}
          </button>
          <button type="button" className={`${ghostButtonClass} shrink-0`} onClick={openPreview}>
            {t("cmsSeo.preview", { defaultValue: "Preview" })}
          </button>
          <button type="button" className={`${ghostButtonClass} shrink-0`} disabled={saving || !dirty} onClick={saveDraft}>
            {t("cmsSeo.saveDraft", { defaultValue: "Save draft" })}
          </button>
          <button type="button" className={`${primaryButtonClass} shrink-0`} disabled={saving} onClick={onPublishClick}>
            {t("cmsSeo.publish", { defaultValue: "Publish" })}
          </button>
        </div>
      </div>

      <LocaleBar page={page} busy={translating} onSwitch={switchTo} onCreate={createTranslation} />

      <div className="flex flex-col lg:flex-row">
        <div className="min-w-0 flex-1 px-4 py-5 sm:px-6">
          <Tabs tabs={tabs} activeTab={tab} onChange={(key) => setTab(key as EditorTab)} />

          <div className={clsx("pt-6", tab !== "content" && "hidden")}>
            <ContentPane page={page} onChange={change} slugWarning={slugWarning} />
          </div>
          <div className={clsx("pt-6", tab !== "seo" && "hidden")}>
            <SeoPane page={page} onChange={change} />
          </div>
          <div className={clsx("pt-6", tab !== "social" && "hidden")}>
            <SocialPane page={page} onChange={change} onPickImage={() => setMediaPickerOpen(true)} />
          </div>
          <div className={clsx("pt-6", tab !== "schema" && "hidden")}>
            <SchemaPane page={page} brand={brand} onChange={change} />
          </div>
          <div className={clsx("pt-6", tab !== "code" && "hidden")}>
            <CustomCodePane page={page} onChange={change} canEdit={canEditCode} />
          </div>
          <div className={clsx("pt-6", tab !== "advanced" && "hidden")}>
            <AdvancedPane
              page={page}
              onChange={change}
              parentCandidates={parentCandidates.filter((candidate) => candidate.id !== page.id)}
              authors={authors}
              onDuplicate={createTranslation}
            />
          </div>
        </div>

        <EditorRail
          page={page}
          score={score}
          onSwitchLocale={switchTo}
          onCreateLocale={createTranslation}
          revalidateSeconds={page.rendering.revalidate_seconds}
          lastPurgeAt={page.rendering.cache_purged_at}
        />
      </div>

      <Modal open={Boolean(pendingSwitch)} handler={() => setPendingSwitch(null)}>
        <div className={`${modalBoxClass} max-w-lg`}>
          <h3 className="mb-3 text-lg font-semibold">
            {t("cmsSeo.unsavedTitle", { defaultValue: "This page has unsaved changes" })}
          </h3>
          <p className="text-sm">
            {t("cmsSeo.unsavedBody", {
              defaultValue: "Switching language leaves this editor. Save the draft first, or leave and lose the changes."
            })}
          </p>
          <div className="modal-action flex-wrap">
            <button type="button" className={ghostButtonClass} onClick={() => setPendingSwitch(null)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              className={ghostButtonClass}
              onClick={() => {
                const target = pendingSwitch
                setPendingSwitch(null)
                if (target) onNavigate(target)
              }}
            >
              {t("cmsSeo.discardAndSwitch", { defaultValue: "Leave without saving" })}
            </button>
            <button
              type="button"
              className={primaryButtonClass}
              onClick={async () => {
                const target = pendingSwitch
                setPendingSwitch(null)
                await saveDraft()
                if (target) onNavigate(target)
              }}
            >
              {t("cmsSeo.saveAndSwitch", { defaultValue: "Save and switch" })}
            </button>
          </div>
        </div>
      </Modal>

      <VersionHistoryModal
        open={historyOpen}
        pageId={page.id}
        onClose={() => setHistoryOpen(false)}
        onRestored={(restored) => {
          setPage(restored)
          setOriginal(restored)
          onSaved()
        }}
      />

      <MediaPickerModal
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onPick={(item) => change({ og_image: item.url })}
      />

      <Modal open={Boolean(publishPrompt)} handler={() => setPublishPrompt(null)}>
        <div className={`${modalBoxClass} max-w-lg`}>
          <h3 className="mb-3 text-lg font-semibold">{t("cmsSeo.slugChanged", { defaultValue: "The slug changed" })}</h3>
          <p className="text-sm">
            {t("cmsSeo.confirmSlugRedirect", {
              defaultValue:
                'This page moves from "{{from}}" to "{{to}}". A 301 from the old URL passes its traffic and ranking to the new one.',
              from: original?.slug ?? "",
              to: page.slug
            })}
          </p>
          <div className="modal-action flex-wrap">
            <button type="button" className={ghostButtonClass} onClick={() => setPublishPrompt(null)}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              className={ghostButtonClass}
              onClick={() => {
                setPublishPrompt(null)
                publish(false)
              }}
            >
              {t("cmsSeo.publishWithoutRedirect", { defaultValue: "Publish without redirect" })}
            </button>
            <button
              type="button"
              className={primaryButtonClass}
              onClick={() => {
                setPublishPrompt(null)
                publish(true)
              }}
            >
              {t("cmsSeo.publishWithRedirect", { defaultValue: "Publish + 301" })}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
